import { Event, EventWithOccurrences } from "gql/generated";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EventWidgetEventDetails } from "shared/components";
import { SmartImage } from "shared/components/atoms";
import { Center, Flex } from "styled-system/jsx";
import { EventStepHeader } from "../EventStepHeader/EventStepHeader";
import { Card, Text, Spinner } from "@ttc3k/trekker";
import { useEventPreview } from "./useEventPreview";
import { getMultilingualString } from "shared/utils";
import { useEventCreateEditContext } from "../EventCreateEditContext/useEventCreateEditContext";

export const EventPreview = () => {
  const { t } = useTranslation("events", {
    keyPrefix: "APP.MODAL.EVENT_PREVIEW",
  });

  const {
    event,
    isOrganizationUser,
    lang,
    locale,
    mode,
    defaultOrganizationId,
    allLocations,
  } = useEventCreateEditContext();

  /**
   * The orgs the chosen host is actively connected to. Memoized so the fetch
   * effect isn't re-triggered by a new array identity on every render.
   */
  const hostActiveOrganizationIds = useMemo(() => {
    const hostEntityId = event?.access?.operator?._entityId;
    if (!hostEntityId) return [];
    return (
      allLocations
        .find(location => location.value === hostEntityId)
        ?.data.enrollments.filter(enrollment => enrollment.isActive)
        .map(enrollment => enrollment.organizationId) ?? []
    );
  }, [allLocations, event?.access?.operator?._entityId]);

  const {
    eventWithOccurrences,
    organizations,
    organizationsLoading,
    contentBoxProps,
    carouselBoxProps,
  } = useEventPreview({
    defaultOrganizationId,
    event: event as Event,
    mode,
    hostActiveOrganizationIds,
  });

  return (
    <Flex flexDir="column" gap="300">
      {mode !== "approve" && mode !== "view" && (
        <EventStepHeader
          step={5}
          title={t("TITLE")}
          description={t("DESCRIPTION")}
        />
      )}

      {!event ? (
        <Center minH="132px">
          <Spinner width={"20px"} height={"20px"} />
        </Center>
      ) : (
        <>
          {/* List of organizations */}
          {mode !== "approve" && mode !== "view" && (
            <Flex flexDir="column" gap="200">
              <Text visual="bodyMedium">{t("SUBMIT_TO")}</Text>
              {organizationsLoading ? (
                <Center
                  bg={"white"}
                  borderColor={"border.light"}
                  borderWidth={"2px"}
                  py="300"
                  px="400"
                  borderRadius="100"
                  minH="132px"
                >
                  <Spinner width={"20px"} height={"20px"} />
                </Center>
              ) : !organizationsLoading && organizations.length === 0 ? (
                <Center
                  bg={"white"}
                  borderColor={"border.light"}
                  borderWidth={"2px"}
                  py="300"
                  px="400"
                  borderRadius="100"
                  minH="132px"
                >
                  <Text visual="bodyMedium" color="text.destructive.dark">
                    {t("NO_ORGANIZATIONS_ERROR")}
                  </Text>
                </Center>
              ) : (
                !organizationsLoading &&
                organizations?.map(organization => {
                  const logo = organization.logo;
                  return (
                    <Card.Surface
                      key={organization._id}
                      bg={"white"}
                      borderColor={"border.light"}
                      borderWidth={"2px"}
                      minH="132px"
                    >
                      <Card.Body pl="300" pr="400" py={"300"}>
                        <Card.BodyContent gap={"500"} alignItems={"center"}>
                          <Flex gap={"300"} alignItems={"center"}>
                            {logo && (
                              <SmartImage
                                image={logo}
                                width={80}
                                shape="rounded"
                                isLogo={true}
                              />
                            )}
                            <Flex
                              flexDir={"column"}
                              gap={"25"}
                              pl={logo ? "300" : undefined}
                              borderLeft={logo ? "2px" : undefined}
                              borderColor={"border.light"}
                              borderStyle={"solid"}
                            >
                              <Text visual={"bodySemiBold"} color={"text.dark"}>
                                {getMultilingualString(organization.name)}
                              </Text>
                              {organization.contact?.links?.website && (
                                <Text
                                  visual={"smallMedium"}
                                  color={"text.light"}
                                >
                                  {organization.contact?.links?.website}
                                </Text>
                              )}
                            </Flex>
                          </Flex>
                        </Card.BodyContent>
                      </Card.Body>
                    </Card.Surface>
                  );
                })
              )}
            </Flex>
          )}

          {eventWithOccurrences && eventWithOccurrences._id && (
            <Flex flexDir="column" gap="250">
              {mode !== "approve" && mode !== "view" && (
                <Flex flexDir="column" gap="100">
                  <Text visual="bodySemiBold">{t("PREVIEW.TITLE")}</Text>
                  <Text visual="bodyRegular" color="text.mid">
                    {t("PREVIEW.DESCRIPTION")}
                  </Text>
                </Flex>
              )}
              <EventWidgetEventDetails
                event={eventWithOccurrences as EventWithOccurrences}
                lang={lang}
                locale={locale}
                contentBoxProps={contentBoxProps}
                carouselBoxProps={carouselBoxProps}
                organizationId={defaultOrganizationId}
                viewOnly={true}
                enableNonOperatorWarning={isOrganizationUser}
              />
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};
